<script setup lang="ts">
import EventPrice from "./EventPrice.vue";
import { IMarket } from "~/types/sports/market";
import { useBetbook } from "~/store/betbook";
import t from "~/utils/translate";
import { IOutcome } from "~/types/sports/outcome";
import sportUrlBuilder from "~/utils/sport/sportUrlBuilder";
import EventMarketTotalGoalDropdown from "./EventMarketTotalGoalDropdown.vue";
import { useBetslip } from "~/store/betslip";
import { totalGoalMarkets } from "~/utils/constants";
import slugify from "~/utils/sport/slugify";

interface Props {
	market: IMarket | undefined;
	eventId: number;
	index?: number;
	isLast: boolean;
}

const betslip = useBetslip();
const props = defineProps<Props>();
const isTotalGoals = computed(() => (props.index !== undefined ? !!totalGoalMarkets[store.displayMarkets[props.index || 0].name] : false));
const totalGoal = ref("");
const store = useBetbook();
const event = computed(() => store.events.get(props.eventId));
const market = computed(() =>
	totalGoal.value ? [...store.markets.values()].find((x) => x.name === totalGoal.value && x.eventId === props.eventId) : props.market
);
const outcomes = computed(() =>
	totalGoal.value && isTotalGoals.value
		? [...store.outcomes.values()]
				.filter((x) => (x.originalMarketId ? x.originalMarketId === market?.value?.marketId : x.marketId === market.value?.marketId))
				.sort((a, b) => a.index - b.index)
				.map((x) => x.outcomeId)
		: props.market
		? store.pointers.outcomes.get(props.market.marketId)
		: []
);
const route = useRoute();
const { fixtureType } = route.params;

const isDisabled = computed(() => {
	if (isTotalGoals.value) {
		return (
			!market.value ||
			!market.value.isActive ||
			market.value.isSuspended ||
			!market.value.shouldDisplay ||
			!outcomes.value?.length ||
			(event.value && !store.isEventActive(event.value)) ||
			!event.value?.shouldDisplay
		);
	}

	return (
		!props.market ||
		!props.market.isActive ||
		props.market.isSuspended ||
		!props.market.shouldDisplay ||
		!outcomes.value?.length ||
		(event.value && !store.isEventActive(event.value)) ||
		!event.value?.shouldDisplay
	);
});

onMounted(() => {
	if (isTotalGoals.value) totalGoal.value = store.displayMarkets[props.index || 0].name;
});

onMounted(() => {
	try {
		if (betslip.selection.length && isTotalGoals.value) {
			betslip.selection.forEach((x) => {
				if (x.eventId === event.value?.eventId && totalGoalMarkets[betslip.markets.get(x.marketId)?.name || ""]) {
					const marketName = betslip.markets.get(x.marketId)?.name;
					if (marketName) totalGoal.value = marketName;
				}
			});
		}
	} catch (e) {
		// Do nothing... return display default
	}
});

onUnmounted(() => {
	totalGoal.value = "";
});
</script>

<template>
	<div v-if="!isDisabled && outcomes && outcomes.length <= 3" class="relative flex w-full gap-1 md:items-center">
		<EventMarketTotalGoalDropdown v-if="isTotalGoals" :event="event" :active="totalGoal" @set-active="(val) => (totalGoal = val)" />
		<EventPrice
			v-for="outcome in outcomes"
			:key="outcome"
			:price="store.prices.get(outcome)"
			:outcome="store.outcomes.get(outcome) as IOutcome"
			:eventId="eventId"
			:market="market"
		/>
		<div v-if="!isLast" class="w-[1px] h-6 bg-light-300 dark:bg-dark-500 absolute -right-1.5 top-1/2 -translate-y-1/2" />
	</div>
	<div v-else class="relative grid grid-cols-3 gap-1">
		<EventMarketTotalGoalDropdown v-if="isTotalGoals" :event="event" :active="totalGoal" @set-active="(val) => (totalGoal = val)" />
		<div
			:class="[
				'flex flex-1 h-[52px] md:h-8  items-center justify-center rounded-md border bg-identity-50 dark:bg-[transparent] border-identity-100 dark:border-dark-400 cursor-pointer w-full',
				isTotalGoals ? ' col-span-2' : ' col-span-3',
				market?.isSuspended && 'bg-light-500 border-light-500 dark:border-dark-400 dark:bg-dark-400',
			]"
		>
			<span v-if="market?.isSuspended">{{ t("suspended") }}</span>
			<NuxtLink
				rel="ugc noindex nofollow"
				:href="
					sportUrlBuilder({
						fixtureType: fixtureType,
						sport: event?.sportId,
						event: {
							awayTeam: event?.awayTeam ?? '',
							homeTeam: event?.homeTeam ?? '',
							eventId: event?.eventId ?? 0,
							leagueId: event?.leagueId ?? '',
							regionId: event?.regionId ?? '',
							expectedEndEpoch: event?.expectedEndEpoch ?? 0,
							expectedStartEpoch: event?.expectedStartEpoch ?? 0,
						},
					})
				"
				class="flex items-center justify-center w-full h-full base-text"
				v-else
				>{{ t("more-bets") }}</NuxtLink
			>
		</div>
	</div>
</template>
